import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Result, DatePicker } from "antd";
import {
   CopyFilled,
   DeleteFilled,
   DownOutlined,
   EditFilled,
   InfoCircleOutlined
} from "@ant-design/icons";
import { t } from "i18next";
import {
   Dropdown,
   Divider,
   Menu,
   Row,
   Col,
   Table,
   Breadcrumb,
   Button,
   Input,
   SearchOutlined,
   PlusOutlined
} from "common/elements";
import { Util } from "common/util";

export default function withDataTable({
   title = "",
   updateLink = "",
   pathName = "/",
   columns = [],
   defaultPageSize = 20,
   permission = "PDD",
   getData = () => { },
   showPagination = true,
   tableScrollY = null,
   confirmSoftDelete = (id) => { },
   componentWillMount = () => { },
   handleSearch = (e) => { }
}) {
   return () => {
      const [datas, setDatas] = React.useState([]);
      const [loading, setLoading] = React.useState(true);
      const params = new URLSearchParams(document.location.search);
      const navigate = useNavigate();
      let noPermission = false;
      let timer = null;

      const fetchList = () => {
         setLoading(true);
         getData()
            .then((response) => {
               setDatas(response);
            })
            .catch(error => {
               if (error.response.status === 403) {
                  localStorage.removeItem("ACCESS_TOKEN");
                  navigate("/signin");
               }
            })
            .finally(() => setLoading(false));
      };

      const onSearch = (e) => {
         clearTimeout(timer);
         handleSearch(e);
         timer = setTimeout(() => {
            fetchList();
         }, 1000);
      };

      const onChangeYear = (value) => {
         const params = new URLSearchParams(document.location.search);
         if (value) {
            const year = value.format("YYYY");
            params.set("year", year);
         } else {
            params.delete("year");
         }

         (new Util()).pushParamsToURL(pathName, params.toString());

         fetchList();
      };

      const onDelete = (id) => {
         Util.getInstance()
            .sweetAlertConfirm(t("text_confrim"), "You won't be able to revert this!", ["Cancel", "Yes, delete it!"])
            .then((willDelete) => {
               if (willDelete) {
                  confirmSoftDelete(id).then(() => {
                     Util.getInstance().publishEvent("fetchData");
                     fetchList()
                  });
               }
            });
      };

      const onTableChange = (newPagination, filters, sorter) => {
         fetchList({
            sortField: sorter.field,
            sortOrder: sorter.order,
            pagination: newPagination,
            ...filters,
         });
      };

      const onChangePagination = (page, pageSize) => {
         const params = new URLSearchParams(document.location.search);
         params.set("current", page);
         params.set("size", pageSize);
         Util.getInstance().pushParamsToURL(pathName, params.toString());
         fetchList();
      };

      const onShowSizeChange = (current, size) => {
         const params = new URLSearchParams(document.location.search);
         params.set("current", current);
         params.set("size", size);
         Util.getInstance().pushParamsToURL(pathName, params.toString());
         fetchList();
      };

      React.useMemo(() => {
         componentWillMount();
      }, []);

      React.useEffect(() => {
         fetchList();
         // eslint-disable-next-line
      }, []);

      const pagination = {
         current: params.has("current") ? parseInt(params.get("current")) : 1,
         defaultPageSize,
         pageSizeOptions: ["20", "30", "50", "100"],
         total: datas.pagination?.total,
         showSizeChanger: true,
         locale: { items_per_page: "" },
         onChange: onChangePagination,
         onShowSizeChange,
         showTotal: (total, range) => `${t("text_display")} ${range[0]}-${range[1]} ${t("text_out_of")} ${total}`,
      };

      if (permission && Util.getInstance().hasNoPermission("view", permission)) {
         if (permission === "location") {
            noPermission = false;
         } else {
            noPermission = true;
         }
      }

      if (noPermission) {
         return (
            <Result
               status="403"
               title="403"
               subTitle={t("text_sorry_you_are_not_authorized_to_access_this_page")}
            />
         );
      }

      return (
         <>
            <Row style={styles.breadCream}>
               <Col span={24}>
                  <Breadcrumb>
                     <Breadcrumb.Item
                        onClick={() => navigate("/")}
                        style={{ cursor: "pointer" }}
                     >
                        {t("text_dashboard")}
                     </Breadcrumb.Item>
                     <Breadcrumb.Item style={{ color: "#1d8747" }}>
                        {title}
                     </Breadcrumb.Item>
                  </Breadcrumb>
               </Col>
            </Row>

            <Row style={{ padding: 15 }}>
               <Col span={8}>
                  <Title title={title} />
               </Col>
               <Col span={16} style={{ textAlign: "right", bottom: "5px" }}>
                  <Input
                     placeholder={t("text_search")}
                     defaultValue={params.get("search")}
                     prefix={<SearchOutlined />}
                     style={styles.generalSearch}
                     allowClear={true}
                     onChange={onSearch}
                  />

                  <DatePicker
                     name="yearOfObjective"
                     placeholder={"Select year"}
                     format={"YYYY"}
                     picker="year"
                     style={{ marginLeft: 10, marginRight: 10, width: 150 }}
                     onChange={onChangeYear}
                  />

                  {
                     Util.getInstance().hasPermission("create objective", permission) && 
                     <Link to={`/performance/pdd/create?year=${params.get("year")}&type=object-setting`} className="ant-btn ant-btn-primary"><PlusOutlined /> New Objective</Link>
                  }
               </Col>
            </Row>

            <div className="data-table" style={{ flex: 1, display: "block" }}>
               <Table
                  dataSource={datas.data}
                  pagination={showPagination ? pagination: false}
                  onChange={onTableChange}
                  rowKey={(record) => `${record.id}`}
                  scroll={datas.pagination && datas.pagination.count > 10 ? { y: tableScrollY ? tableScrollY : window.innerHeight - 310 } : {}}
                  columns={[
                     ...columns,
                     {
                        title: t("text_action"),
                        dataIndex: "id",
                        key: "id",
                        width: 100,
                        render: (id, record) => {
                           return <div id="btn-action">
                              {
                                    Util.getInstance().getCurrentUser().id === record.employeeId && 
                                    <>
                                       <Link 
                                          to={`${updateLink}/${id}?type=object-setting`}
                                          className="ant-btn ant-btn-primary ant-btn-icon-only"
                                          title="Edit"
                                          style={{
                                             marginLeft: 5,
                                             height: 35,
                                             width: 35,
                                             borderRadius: 35,
                                             border: "none"
                                          }}
                                       >
                                          <EditFilled style={{ color: "#fff" }} />
                                       </Link>
                                       <Link 
                                          to={`${updateLink}/${id}?type=object-setting&action=duplicate`}
                                          className="ant-btn ant-btn-circle ant-btn-default ant-btn-icon-only"
                                          title="Duplicate"
                                          style={{
                                             marginLeft: 5,
                                             height: 35,
                                             width: 35
                                          }}
                                       >
                                          <CopyFilled />
                                       </Link>
                                    </>
                              }
                              {
                                 Util.getInstance().getCurrentUser().id !== record.employeeId && 
                                 <>
                                    <Button
                                       danger
                                       title={t("text_delete")}
                                       icon={<DeleteFilled style={{ color: "#fff" }} />}
                                       shape="circle"
                                       type="primary"
                                       style={{
                                          marginLeft: 5,
                                          height: 35,
                                          width: 35,
                                          borderRadius: 35,
                                          border: "none"
                                       }}
                                       onClick={() => onDelete(id)}
                                    />
                                    <Link 
                                       to={`${updateLink}/${id}?type=review-objective`}
                                       className="ant-btn ant-btn-circle ant-btn-default ant-btn-icon-only"
                                       title="Review Objective"
                                       style={{
                                          marginLeft: 5,
                                          height: 35,
                                          width: 35
                                       }}
                                    >
                                       <InfoCircleOutlined />
                                    </Link>
                                 </>
                              }
                           </div>;
                        },
                        renders: (id, record) => {
                           return <Dropdown overlay={
                              <Menu>
                                 {
                                    Util.getInstance().getCurrentUser().id !== record.employeeId && 
                                    <Menu.Item key={0} onClick={() => onDelete(id)}>
                                       Delete
                                    </Menu.Item>
                                 }
                                 {
                                    Util.getInstance().getCurrentUser().id === record.employeeId && 
                                    <>
                                       <Menu.Item key={1}>
                                          <Link to={`${updateLink}/${id}?type=object-setting`}>Edit</Link>
                                       </Menu.Item>
                                       <Menu.Item key={6}>
                                          <Link to={`${updateLink}/${id}?type=object-setting&action=duplicate`}>Duplicate</Link>
                                       </Menu.Item>
                                    </>
                                 }
                                 <Divider style={{margin: 0}} />
                                 <Menu.Item key={2}>
                                    <Link to={`${updateLink}/${id}?type=review-objective`}>Review for Approval</Link>
                                 </Menu.Item>
                                 <Menu.Item key={3}>
                                    <Link to={`/performance/pdd/quarterly-review/${id}`}>Quarterly Review</Link>
                                 </Menu.Item>
                                 <Divider style={{margin: 0}} />
                                 {
                                    Util.getInstance().getCurrentUser().id === record.employeeId && 
                                    <Menu.Item key={4}>
                                       <Link to={`${updateLink}/${id}?type=self-evaluation`}>Self Evaluation</Link>
                                    </Menu.Item>
                                 }
                                 <Menu.Item key={5}>
                                    <Link to={`${updateLink}/${id}?type=evaluation`}>Evaluation Discussion</Link>
                                 </Menu.Item>
                              </Menu>
                            }>
                              <Button
                                style={{ marginLeft: 10 }}
                              >
                                 Options
                                 <DownOutlined style={{fontSize: 14}} />
                              </Button>
                            </Dropdown>
                        }
                     },
                  ]}
                  loading={loading}
               />
            </div>
         </>
      );
   };
}

function Title({ title }) {
   return <h4 style={{ marginBottom: 0, fontWeight: 600 }}>{title}</h4>;
}

const styles = {
   generalSearch: {
      width: 200,
   },
   headerFilter: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
   },
   breadCream: {
      paddingBottom: 15,
      backgroundColor: "#EFF3F6",
   },
   style: {
      background: "#0092ff",
      padding: "8px 0",
   },
};
